import type { ExpandStatus } from "@stryker/core/types/expandable";
import type { MechanicalJob } from "@stryker/core/types/jobs";
import type {
  ChecksSection,
  CreateUpdateMechanicalJobSections,
  DrawingNotesSection,
  EnclosureDetailsSection,
  FinalDesignSection,
  JointEfficiencySection,
  MechanicalJobDetailsSection,
  PostConstructionTestingRequirementsSection,
  RepairTypeSection,
  SealingDetailsSection,
  StrongbackSection,
  StrongbackTypeLocationSection,
} from "@stryker/core/types/mechanicalJobs";
import type { ComponentDetailsSection } from "@stryker/core/types/mechanicalSections";
import type {
  AdditionalJobDetailsSection,
  ContactInformationSection,
  DefectDetailsData,
  DefectDetailsSection,
  DesignConditionsSection,
  NotesSection,
} from "@stryker/core/types/sections";
import { isEmpty } from "remeda";

const getMechanicalJobDetails = (job: MechanicalJob): MechanicalJobDetailsSection => {
  return {
    status: job.is_draft ? "done" : "open",
    isDataFilled: !!job.is_draft,
    data: {
      branch: job.branch,
      job_number: job.job_number ?? null,
      customer_number: job.customer_number ?? "",
      plant_name: job.plant_name,
      service_name: job.service_name,
      design_code: job.design_code || null,
    },
  };
};

const getAdditionalJobDetails = (
  job: MechanicalJob,
  designConditionsFilled: boolean,
  additionalJobDetailsAreFilled: boolean
): AdditionalJobDetailsSection => {
  let status: ExpandStatus = "blocked";
  if (additionalJobDetailsAreFilled || designConditionsFilled) {
    status = "done";
  } else if (job.plant_name) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: additionalJobDetailsAreFilled || designConditionsFilled,
    addMore: additionalJobDetailsAreFilled,
    data: {
      unit: job.unit,
      line_id: job.line_id,
      salesman: job.salesman ?? undefined,
    },
  };
};

const getContactInformation = ({
  job,
  contactInfoFilled,
  additionalJobDetailsAreFilled,
  designConditionsFilled,
}: {
  job: MechanicalJob;
  additionalJobDetailsAreFilled: boolean;
  contactInfoFilled: boolean;
  designConditionsFilled: boolean;
}): ContactInformationSection => {
  let status: ExpandStatus = "blocked";
  if (contactInfoFilled || designConditionsFilled) {
    status = "done";
  } else if (additionalJobDetailsAreFilled || job.plant_name) {
    status = "canBeOpened";
  }
  return {
    status,
    isDataFilled: contactInfoFilled || designConditionsFilled,
    addMore: contactInfoFilled,
    data: {
      contact_info_name: job.contact_info_name,
      contact_info_email: job.contact_info_email,
      contact_info_phone: job.contact_info_phone,
    },
  };
};

const getDesignConditions = ({
  job,
  designConditionsFilled,
  contactInfoFilled,
}: {
  job: MechanicalJob;
  designConditionsFilled: boolean;
  contactInfoFilled: boolean;
}): DesignConditionsSection => {
  let status: ExpandStatus = "blocked";
  if (designConditionsFilled) {
    status = "done";
  } else if (contactInfoFilled || job.plant_name) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: designConditionsFilled,
    data: {
      design_pressure: job.design_pressure,
      design_temperature: job.design_temperature,
      unit_for_design_temperature: job.unit_for_design_temperature,
      injection_pressure_pct_increase: job.injection_pressure_pct_increase ?? null,
    },
  };
};

const getRepairType = ({
  job,
  repairTypeFilled,
  designConditionsFilled,
}: {
  job: MechanicalJob;
  repairTypeFilled: boolean;
  designConditionsFilled: boolean;
}): RepairTypeSection => {
  let status: ExpandStatus = "blocked";
  if (repairTypeFilled) {
    status = "done";
  } else if (designConditionsFilled) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: repairTypeFilled,
    data: {
      repairType: job.repair_type as "straight_line" | "tee" | "long_radius" | "flange_clamp" | undefined,
    },
  };
};

const getEnclosureDetails = ({
  job,
  enclosureDetailsFilled,
  repairTypeFilled,
}: {
  job: MechanicalJob;
  enclosureDetailsFilled: boolean;
  repairTypeFilled: boolean;
}): EnclosureDetailsSection => {
  let status: ExpandStatus = "blocked";
  if (enclosureDetailsFilled) {
    status = "done";
  } else if (repairTypeFilled) {
    status = "canBeOpened";
  }

  // Handle different repair types - flange_clamp has different data structure
  const isFlangeClamb = job.repair_type === "flange_clamp";

  return {
    status,
    isDataFilled: enclosureDetailsFilled,
    data: isFlangeClamb
      ? {
          flange_clamp: job.flange_clamp || "",
          stud_bolts: job.stud_bolts || "",
          match_enclosure_to_component_material: job.match_enclosure_to_component_material,
        }
      : {
          shells: job.shells || "",
          endplates: job.endplates || "",
          ears: job.ears || "",
          stud_bolts: job.stud_bolts || "",
          riser_shells: job.riser_shells || "",
          match_enclosure_to_component_material: job.match_enclosure_to_component_material,
        },
  };
};

const getStrongbacks = ({
  job,
  strongbacksAnswered,
  shouldAddStrongbacks,
  enclosureDetailsFilled,
}: {
  job: MechanicalJob;
  strongbacksAnswered: boolean;
  shouldAddStrongbacks: boolean;
  enclosureDetailsFilled: boolean;
}): StrongbackSection => {
  const strongbackMaterial =
    (
      job as MechanicalJob & {
        strongback_material?: StrongbackSection["data"];
        strongback_material_details?: StrongbackSection["data"];
        strongback_type_and_location_detail?: StrongbackTypeLocationSection["data"];
      }
    ).strongback_material ??
    (job as any).strongback_material_details ??
    {};
  const matchToEnclosure =
    strongbackMaterial?.match_strongback_to_enclosure ??
    (job as any).strongback_type_and_location_detail?.match_strongback_to_enclosure;
  const strongbacksId = strongbackMaterial?.strongbacks ?? "";
  const strongbackStudBoltsId = strongbackMaterial?.strongbacks_stud_bolts ?? "";

  // "Answered" (yes or no) marks the question as done; "shouldAddStrongbacks" is the actual
  // answer and is what decides whether the material sub-questions should render.
  let status: ExpandStatus = "blocked";
  if (strongbacksAnswered) {
    status = "done";
  } else if (enclosureDetailsFilled) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: strongbacksAnswered,
    addMore: shouldAddStrongbacks,
    data: {
      strongbacks: strongbacksId?.toString() ?? "",
      strongbacks_stud_bolts: strongbackStudBoltsId?.toString() ?? "",
      match_strongback_to_enclosure: matchToEnclosure,
    },
  };
};

const normalizeBoolean = (value: unknown) => {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return undefined;
};

const getStrongbackTypeLocation = ({
  job,
  typeLocationFilled,
  shouldAddStrongbacks,
}: {
  job: MechanicalJob;
  typeLocationFilled: boolean;
  shouldAddStrongbacks: boolean;
}): StrongbackTypeLocationSection => {
  const nestedTypeLocation =
    (
      job as unknown as {
        strongback_type_location?: Partial<StrongbackTypeLocationSection["data"]>;
        strongback_type_and_location_detail?: Partial<StrongbackTypeLocationSection["data"]>;
      }
    ).strongback_type_location ?? (job as any).strongback_type_and_location_detail;
  const matchToEnclosure = nestedTypeLocation?.match_strongback_to_enclosure;

  const strongbackHeader = normalizeBoolean(nestedTypeLocation?.strongback_header);
  const strongbackRiser = normalizeBoolean(nestedTypeLocation?.strongback_riser);
  const strongbackTop = normalizeBoolean(nestedTypeLocation?.strongback_top);
  const strongbackBottom = normalizeBoolean(nestedTypeLocation?.strongback_bottom);
  const normalizeStrongback = (
    entry?: Partial<StrongbackTypeLocationSection["data"]["strongbacks"][number]>
  ): StrongbackTypeLocationSection["data"]["strongbacks"][number] => {
    return {
      type: entry?.type ?? "",
      distance: entry?.distance ?? undefined,
      unit_for_distance: entry?.unit_for_distance || "Inches",
      flange_nps: entry?.flange_nps ?? "",
      flange_rating: entry?.flange_rating ?? "",
      custom_diameter: entry?.custom_diameter,
      raised_face_od: entry?.raised_face_od,
      flange_num_bolt_holes: entry?.flange_num_bolt_holes,
      flange_stud_size: entry?.flange_stud_size,
      flange_bolt_circle: entry?.flange_bolt_circle,
    };
  };

  const strongbacksArraySource = nestedTypeLocation?.strongbacks ?? [];

  const normalizedStrongbacks = Array.from({ length: 4 }, (_, index) =>
    normalizeStrongback(strongbacksArraySource[index])
  );

  const teeStrongbackFields =
    job.repair_type === "tee"
      ? {
          strongback_header: strongbackHeader,
          strongback_riser: strongbackRiser,
        }
      : {};

  const strongback90Fields =
    job.repair_type === "long_radius"
      ? {
          strongback_top: strongbackTop,
          strongback_bottom: strongbackBottom,
        }
      : {};

  let status: ExpandStatus = "blocked";
  if (typeLocationFilled) {
    status = "done";
  } else if (shouldAddStrongbacks) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: typeLocationFilled,
    data: {
      match_strongback_to_enclosure: matchToEnclosure,
      strongbacks: normalizedStrongbacks,
      ...teeStrongbackFields,
      ...strongback90Fields,
    },
  };
};

const getPostConstructionTestingRequirements = ({
  job,
  postConstructionTestingRequirementsFilled,
  strongbacksFilled,
}: {
  job: MechanicalJob;
  postConstructionTestingRequirementsFilled: boolean;
  strongbacksFilled: boolean;
}): PostConstructionTestingRequirementsSection => {
  let status: ExpandStatus = "blocked";
  if (postConstructionTestingRequirementsFilled) {
    status = "done";
  } else if (strongbacksFilled) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: postConstructionTestingRequirementsFilled,
    data: {
      post_construction_testing_requirements: job.post_construction_testing_requirements || [],
    },
  };
};

const getSealingDetails = ({
  job,
  sealingDetailsFilled,
  postConstructionTestingRequirementsFilled,
}: {
  job: MechanicalJob;
  sealingDetailsFilled: boolean;
  postConstructionTestingRequirementsFilled: boolean;
}): SealingDetailsSection => {
  let status: ExpandStatus = "blocked";
  if (sealingDetailsFilled) {
    status = "done";
  } else if (postConstructionTestingRequirementsFilled) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: sealingDetailsFilled,
    data: {
      seal_preference: job.seal_preference || "",
      seal_type: job.seal_type || "",
      seal_size: job.seal_size || "",
      seal_material: job.seal_material || "",
      sealant: job.sealant || "",
      sealant_2: job.sealant_2 || "",
    },
  };
};

const getDrawingNotes = ({
  job,
  drawingNotesFilled,
  sealingDetailsFilled,
  postConstructionTestingRequirementsFilled,
  repairType,
}: {
  job: MechanicalJob;
  drawingNotesFilled: boolean;
  sealingDetailsFilled: boolean;
  postConstructionTestingRequirementsFilled: boolean;
  repairType: MechanicalJob["repair_type"];
}): DrawingNotesSection => {
  let status: ExpandStatus = "blocked";
  if (drawingNotesFilled) {
    status = "done";
  } else if (
    sealingDetailsFilled ||
    (repairType === "flange_clamp" && postConstructionTestingRequirementsFilled && !drawingNotesFilled)
  ) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: drawingNotesFilled,
    data: {
      additional_notes: job.additional_notes ?? [],
      // Support legacy drafts that may have stored raw drawing note strings
      new_notes:
        (job as unknown as { new_notes?: DrawingNotesSection["data"]["new_notes"] | undefined }).new_notes ??
        (job.drawing_notes ? job.drawing_notes.map((note) => ({ custom_note: note })) : []),
    },
  };
};

const getChecks = ({
  job,
  checksFilled,
  drawingNotesFilled,
}: {
  job: MechanicalJob;
  checksFilled: boolean;
  drawingNotesFilled: boolean;
}): ChecksSection => {
  let status: ExpandStatus = "blocked";
  if (checksFilled) {
    status = "done";
  } else if (drawingNotesFilled) {
    status = "canBeOpened";
  }

  // Handle different repair types - tee has additional field
  const isTee = job.repair_type === "tee";

  const baseData = {
    is_the_service_hf_acid: job.is_the_service_hf_acid,
    is_the_service_wet_h2s: job.is_the_service_wet_h2s,
    is_this_a_flare_line: job.is_this_a_flare_line,
    does_the_line_vibrate_or_hammer: job.does_the_line_vibrate_or_hammer,
    do_you_want_to_add_external_pressure_calculation: job.do_you_want_to_add_external_pressure_calculation,
    do_you_want_to_have_small_cross_section: job.do_you_want_to_have_small_cross_section,
    do_you_need_minimum_clearance: job.do_you_need_minimum_clearance,
    min_id_clearance: job.min_id_clearance ?? null,
    do_you_want_to_add_mdmt_to_package: job.do_you_want_to_add_mdmt_to_package,
    mdmt: job.mdmt ?? null,
    is_this_a_redesign: job.is_this_a_redesign,
    is_this_revision: job.is_this_revision,
    revision: job.revision ?? null,
    do_you_wish_to_change_default_joint_efficiency: (job as any).do_you_wish_to_change_default_joint_efficiency,
    do_you_wish_to_change_default_checked_by: job.do_you_wish_to_change_default_checked_by,
    checked_by_first_name: job.checked_by_first_name ?? null,
    checked_by_last_name: job.checked_by_last_name ?? null,
  };

  return {
    status,
    isDataFilled: checksFilled,
    data: isTee
      ? {
          ...baseData,
          do_you_want_to_add_area_of_reinforcement_calculation:
            job.do_you_want_to_add_area_of_reinforcement_calculation,
          min_id_clearance_2: job.min_id_clearance_2 ?? null,
        }
      : baseData,
  };
};

const getComponentDetails = ({
  job,
  componentDetailsFilled,
  checksFilled,
}: {
  job: MechanicalJob;
  componentDetailsFilled: boolean;
  checksFilled: boolean;
}): ComponentDetailsSection => {
  let status: ExpandStatus = "blocked";
  if (componentDetailsFilled) {
    status = "done";
  } else if (checksFilled) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: componentDetailsFilled,
    data: {
      component_options: job.component_options || [],
      component_material: job.component_material,
      components_data: job.components_data || [
        {
          component_nps: null,
          component_diameter: null,
          unit_for_component_diameter: null,
          component_thickness: null,
          unit_for_component_thickness: null,
          component_schedule: null,
        },
      ],
    },
  };
};

const getDefectDetails = ({
  job,
  componentDetailsFilled,
  defectDetailsFilled,
}: {
  job: MechanicalJob;
  componentDetailsFilled: boolean;
  defectDetailsFilled: boolean;
}): DefectDetailsSection => {
  let status: ExpandStatus = "blocked";
  if (defectDetailsFilled) {
    status = "done";
  } else if (componentDetailsFilled) {
    status = "canBeOpened";
  }

  const jobDefects = (job.defects as any[]) || [];
  let defects = jobDefects;

  // Mapping for straight_line defect field names if they differ
  if (job.repair_type === "straight_line") {
    defects = jobDefects.map((d) => ({
      ...d,
      total_defect_length: d.total_defect_length ?? d.defect_length_total ?? "",
      unit_for_total_defect_length: d.unit_for_total_defect_length ?? d.unit_for_defect_length ?? "Inches",
      desired_overall_length: d.desired_overall_length ?? d.defect_length_wrap ?? "",
      desired_inside_length: d.desired_inside_length ?? undefined,
      unit_for_desired_overall_length:
        d.unit_for_desired_overall_length ??
        d.unit_for_desired_inside_length ??
        d.unit_for_defect_length_wrap ??
        "Inches",
    }));
  }

  if (job.repair_type === "flange_clamp") {
    const firstDefect = (jobDefects[0] as Record<string, unknown> | undefined) ?? {};
    const flangeClampJob = job as MechanicalJob & {
      thickness_gap_calc?: number | string | null;
      drill_flange_to_stud_cavity?: boolean | string | null;
    };
    const drillFlangeToStudCavity =
      normalizeBoolean(firstDefect.drill_flange_to_stud_cavity ?? flangeClampJob.drill_flange_to_stud_cavity) ?? null;

    return {
      status,
      isDataFilled: defectDetailsFilled,
      data: {
        flange_clamp_type: (job.flange_clamp_type ?? firstDefect.flange_clamp_type ?? null) as string | null,
        thickness_gap: (firstDefect.thickness_gap ?? job.thickness_gap ?? null) as number | string | null,
        thickness_gap_calc: (firstDefect.thickness_gap_calc ?? flangeClampJob.thickness_gap_calc ?? null) as
          | number
          | string
          | null,
        unit_for_thickness_gap: (firstDefect.unit_for_thickness_gap ??
          job.unit_for_thickness_gap ??
          "Inches") as string,
        desired_center_groove_size: (firstDefect.desired_center_groove_size ??
          job.desired_center_groove_size ??
          null) as number | string | null,
        unit_for_desired_center_groove_size: (firstDefect.unit_for_desired_center_groove_size ??
          job.unit_for_desired_center_groove_size ??
          "Inches") as string,
        drill_flange_to_stud_cavity: drillFlangeToStudCavity,
        repair_length_repair_kit_sizes: job.repair_length_repair_kit_sizes || [],
        repair_length_options: job.repair_length_options || [],
      } as DefectDetailsData,
    };
  }

  return {
    status,
    isDataFilled: defectDetailsFilled,
    data: {
      repair_length_repair_kit_sizes: job.repair_length_repair_kit_sizes || [],
      repair_length_options: job.repair_length_options || [],
      defects: defects,
    } as DefectDetailsData,
  };
};

const getNotes = (job: MechanicalJob): NotesSection => {
  const hasNotes = !!job.additional_notes.length;
  return {
    status: hasNotes ? "done" : "canBeOpened",
    isDataFilled: hasNotes,
    data: {
      additional_notes: job.additional_notes,
      new_notes: [],
    },
  };
};

const getFinalDesign = ({ defectDetailsFilled }: { defectDetailsFilled: boolean }): FinalDesignSection => {
  let status: ExpandStatus = "blocked";
  if (defectDetailsFilled) {
    status = "canBeOpened";
  }

  return {
    status,
    isDataFilled: false,
    data: {},
  };
};

export function createInitialState(job: MechanicalJob): CreateUpdateMechanicalJobSections {
  const jobDetails = getMechanicalJobDetails(job);
  const designConditionsFilled = !!job.unit_for_design_temperature;

  const additionalJobDetailsAreFilled = !!(job.unit || job.line_id);
  const additionalJobDetails = getAdditionalJobDetails(job, designConditionsFilled, additionalJobDetailsAreFilled);

  const contactInfoFilled = !!(job.contact_info_email || job.contact_info_phone || job.contact_info_name);
  const contactInformation = getContactInformation({
    job,
    contactInfoFilled,
    additionalJobDetailsAreFilled,
    designConditionsFilled,
  });

  const designConditions = getDesignConditions({
    job,
    designConditionsFilled,
    contactInfoFilled,
  });

  const repairTypeFilled = !!job.repair_type;
  const repairType = getRepairType({ job, repairTypeFilled, designConditionsFilled });

  const enclosureDetailsFilled = !!(job.shells || job.flange_clamp);
  const enclosureDetails = getEnclosureDetails({ job, enclosureDetailsFilled, repairTypeFilled });

  const strongbackTypeLocationNested =
    (
      job as unknown as {
        strongback_type_location?: StrongbackTypeLocationSection["data"];
        strongback_type_and_location_detail?: StrongbackTypeLocationSection["data"];
      }
    ).strongback_type_location ?? (job as any).strongback_type_and_location_detail;
  const nestedStrongbacks = strongbackTypeLocationNested?.strongbacks ?? [];
  const strongbackArrayHasData = nestedStrongbacks.some(
    (sb: StrongbackTypeLocationSection["data"]["strongbacks"][number]) => {
      return (
        sb?.type ||
        sb?.distance !== undefined ||
        sb?.flange_nps ||
        sb?.flange_rating ||
        sb?.custom_diameter !== undefined ||
        sb?.flange_num_bolt_holes !== undefined ||
        sb?.flange_stud_size !== undefined ||
        sb?.flange_bolt_circle !== undefined
      );
    }
  );

  const typeLocationFilled = !!(
    strongbackTypeLocationNested?.strongback_header !== undefined ||
    strongbackTypeLocationNested?.strongback_riser !== undefined ||
    strongbackTypeLocationNested?.strongback_top !== undefined ||
    strongbackTypeLocationNested?.strongback_bottom !== undefined ||
    strongbackArrayHasData
  );

  const strongbackMaterial =
    (
      job as MechanicalJob & {
        strongback_material?: StrongbackSection["data"];
        strongback_material_details?: StrongbackSection["data"];
        strongback_type_and_location_detail?: StrongbackTypeLocationSection["data"];
      }
    ).strongback_material ??
    (job as any).strongback_material_details ??
    {};
  const matchToEnclosure =
    strongbackMaterial?.match_strongback_to_enclosure ??
    (job as any).strongback_type_and_location_detail?.match_strongback_to_enclosure;
  const strongbacksId = strongbackMaterial?.strongbacks ?? "";
  const strongbackStudBoltsId = strongbackMaterial?.strongbacks_stud_bolts ?? "";
  const hasBothMaterials = !!strongbacksId && !!strongbackStudBoltsId;
  // "Answered" marks the yes/no question itself as done (true even when the answer was "no");
  // "shouldAddStrongbacks" is the actual answer and decides whether material questions render.
  // Trust the explicit flag when the job has one; older jobs without it fall back to inferring
  // both from whatever strongback data happens to be present.
  const strongbacksAnswered =
    job.do_you_wish_to_add_strongbacks !== undefined || hasBothMaterials || typeLocationFilled;
  const shouldAddStrongbacks =
    job.do_you_wish_to_add_strongbacks !== undefined
      ? job.do_you_wish_to_add_strongbacks
      : matchToEnclosure === true
        ? true
        : hasBothMaterials || typeLocationFilled;
  const strongbacksSection = getStrongbacks({
    job,
    strongbacksAnswered,
    shouldAddStrongbacks,
    enclosureDetailsFilled,
  });
  const strongbackTypeLocation = getStrongbackTypeLocation({ job, typeLocationFilled, shouldAddStrongbacks });

  const postConstructionTestingRequirementsFilled = !!job.post_construction_testing_requirements?.length;
  const postConstructionTestingRequirements = getPostConstructionTestingRequirements({
    job,
    postConstructionTestingRequirementsFilled,
    strongbacksFilled: typeLocationFilled,
  });

  const sealingDetailsFilled = !!(
    job.seal_preference ||
    job.seal_type ||
    job.seal_size ||
    job.seal_material ||
    job.sealant ||
    job.sealant_2
  );
  const sealingDetails = getSealingDetails({
    job,
    sealingDetailsFilled,
    postConstructionTestingRequirementsFilled,
  });

  const drawingNotesFilled =
    !!(job.additional_notes && job.additional_notes.length > 0) ||
    !!(job.drawing_notes && job.drawing_notes.length > 0) ||
    !!(job as unknown as { new_notes?: unknown[] | undefined }).new_notes?.length ||
    job.job_status === "Completed";
  const drawingNotes = getDrawingNotes({
    job,
    drawingNotesFilled,
    sealingDetailsFilled,
    postConstructionTestingRequirementsFilled,
    repairType: job.repair_type,
  });

  const checksFilled = !!(
    job.is_the_service_hf_acid !== undefined ||
    job.is_the_service_wet_h2s !== undefined ||
    job.is_this_a_flare_line !== undefined ||
    job.does_the_line_vibrate_or_hammer !== undefined ||
    job.do_you_want_to_add_external_pressure_calculation !== undefined ||
    job.do_you_want_to_have_small_cross_section !== undefined ||
    job.do_you_need_minimum_clearance !== undefined ||
    (job.min_id_clearance !== undefined && job.min_id_clearance !== null) ||
    (job.min_id_clearance_2 !== undefined && job.min_id_clearance_2 !== null) ||
    job.do_you_want_to_add_mdmt_to_package !== undefined ||
    (job.mdmt !== undefined && job.mdmt !== null) ||
    job.is_this_a_redesign !== undefined ||
    job.is_this_revision !== undefined ||
    (job.revision !== undefined && job.revision !== null) ||
    (job as any).do_you_wish_to_change_default_joint_efficiency !== undefined ||
    job.do_you_wish_to_change_default_checked_by !== undefined ||
    (job.checked_by_first_name !== undefined && job.checked_by_first_name !== null) ||
    (job.checked_by_last_name !== undefined && job.checked_by_last_name !== null)
  );
  const checks = getChecks({ job, checksFilled, drawingNotesFilled });

  const componentDetailsFilled = !!(job.component_material || !isEmpty(job.component_options));
  const componentDetails = getComponentDetails({ job, componentDetailsFilled, checksFilled });

  const flangeClampJob = job as MechanicalJob & {
    thickness_gap_calc?: number | string | null;
    drill_flange_to_stud_cavity?: boolean | string | null;
  };
  const hasFlangeClampDefectData =
    job.repair_type === "flange_clamp" &&
    !!(
      job.flange_clamp_type ||
      job.thickness_gap != null ||
      flangeClampJob.thickness_gap_calc != null ||
      job.unit_for_thickness_gap ||
      flangeClampJob.drill_flange_to_stud_cavity != null ||
      !isEmpty(job.defects)
    );
  const defectDetailsFilled = !isEmpty(job.defects) || hasFlangeClampDefectData;
  const defectDetails = getDefectDetails({ defectDetailsFilled, componentDetailsFilled, job });

  const notes = getNotes(job);
  const finalDesign = getFinalDesign({ defectDetailsFilled });

  const jointEfficiencyData = {
    joint_efficiency: (job as any).joint_efficiency ?? null,
    mill_tolerance: (job as any).mill_tolerance ?? null,
    corrosion_allowance: (job as any).corrosion_allowance ?? null,
  };

  const jointEfficiencyFilled =
    jointEfficiencyData.joint_efficiency !== null ||
    jointEfficiencyData.mill_tolerance !== null ||
    jointEfficiencyData.corrosion_allowance !== null;

  const jointEfficiency: JointEfficiencySection = {
    status: jointEfficiencyFilled ? "done" : defectDetailsFilled ? "canBeOpened" : "blocked",
    isDataFilled: jointEfficiencyFilled,
    data: jointEfficiencyData,
  };

  return {
    jobDetails,
    additionalJobDetails,
    contactInformation,
    designConditions,
    repairType,
    enclosureDetails,
    strongbacks: strongbacksSection,
    strongbackTypeLocation,
    postConstructionTestingRequirements,
    sealingDetails,
    drawingNotes,
    checks,
    jointEfficiency,
    componentDetails,
    defectDetails,
    notes,
    finalDesign,
  };
}
